import React, { useMemo, useState } from 'react';
import { Drawer, List, ListItem, ListItemText, Collapse, ListItemIcon, useMediaQuery, Box, Typography, Divider } from '@mui/material';
import { People, Analytics, Event, Payments, TaskOutlined, ExpandLess, ExpandMore, BarChart, PieChart, AccountBalance } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { headerHeight, sidebarWidth } from '../utils/appLayout';

const Sidebar = ({ open, toggleDrawer, selectedReport }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [reportsOpen, setReportsOpen] = useState(false);

  const hiddenPaths = ['/home', '/login', '/register', '/'];
  const hideSidebar = hiddenPaths.includes(currentPath);
  
  const eventId = currentPath.split('/')[2];

  const eventIds = useMemo(() => {
    if (eventId === 'multi-view') {
      const queryParam = new URLSearchParams(location.search).get('eventIds');
      return queryParam ? queryParam.split(',') : [];
    }
    return [];
  }, [eventId, location.search]);

  const toggleReports = () => {
    setReportsOpen(!reportsOpen);
  };

  const handleReportClick = (reportType) => {
    if (eventId === 'multi-view') {
      navigate(`/events/multi-view/reports/${reportType.toLowerCase()}?eventIds=${eventIds.join(',')}`);
    } else {
      navigate(`/events/${eventId}/reports/${reportType.toLowerCase()}`);
    }
    if (isMobile) toggleDrawer();
  };

  const singleMenuItems = [
    { text: 'Planning Pulse', icon: <TaskOutlined />, path: `/events/${eventId}/setup` },
    { text: 'Budget Tracking', icon: <Payments />, path: `/events/${eventId}/budget` },
    { text: 'Task Management', icon: <TaskOutlined />, path: `/events/${eventId}/tasks` },
    { text: 'Guest List Management', icon: <People />, path: `/events/${eventId}/guests` },
    { text: 'Calendar', icon: <Event />, path: `/events/${eventId}/calendar` },
  ];

  const multiViewMenuItems = [
    { text: 'Budget Tracking', icon: <Payments />, path: `/events/multi-view/budget?eventIds=${eventIds.join(',')}` },
    { text: 'Task Management', icon: <TaskOutlined />, path: `/events/multi-view/tasks?eventIds=${eventIds.join(',')}` },
    { text: 'Guest List Management', icon: <People />, path: `/events/multi-view/guests?eventIds=${eventIds.join(',')}` },
    { text: 'Calendar', icon: <Event />, path: `/events/multi-view/calendar?eventIds=${eventIds.join(',')}` },
  ];

  const reportItems = [
    { text: 'Expenses', icon: <BarChart />, type: 'expenses' },
    { text: 'Tasks', icon: <PieChart />, type: 'tasks' },
    { text: 'Splits', icon: <AccountBalance />, type: 'splits' }
  ];

  const menuItems = eventId === 'multi-view' ? multiViewMenuItems : singleMenuItems;

  if (hideSidebar) return null;

  return (
    <Drawer
      sx={{
        width: isMobile ? 'auto' : sidebarWidth,
        flexShrink: 0,
        zIndex: theme.zIndex.drawer,
        '& .MuiDrawer-paper': { 
          width: isMobile ? 'min(320px, calc(100% - 32px))' : sidebarWidth,
          top: headerHeight,
          height: { xs: `calc(100dvh - ${headerHeight.xs}px)`, md: `calc(100dvh - ${headerHeight.md}px)` },
          boxSizing: 'border-box',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(180deg, rgba(18, 18, 18, 0.95) 0%, rgba(24, 24, 24, 0.98) 50%, rgba(30, 30, 30, 1) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.95) 50%, rgba(245, 245, 245, 0.92) 100%)',
          backdropFilter: 'blur(20px)',
          borderRight: theme.palette.mode === 'dark' 
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: theme.palette.mode === 'dark'
            ? '4px 0 20px rgba(0, 0, 0, 0.3)'
            : '4px 0 20px rgba(0, 0, 0, 0.1)',
        },
      }}
      variant={isMobile ? 'temporary' : 'permanent'}
      anchor="left"
      open={isMobile ? open : true}
      onClose={toggleDrawer}
    >
      <Box sx={{ p: 2 }}>

        <List sx={{ px: 1 }}>
          {menuItems.map(({ text, icon, path }) => {
            const isActive = currentPath === path;
            return (
              <ListItem
                component={Link}
                to={path}
                key={text}
                sx={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  borderRadius: 2,
                  mb: 0.5,
                  background: isActive 
                    ? theme.palette.mode === 'dark' 
                      ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.2) 0%, rgba(25, 118, 210, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(25, 118, 210, 0.12) 0%, rgba(25, 118, 210, 0.06) 100%)'
                    : 'transparent',
                  border: isActive 
                    ? `1px solid ${theme.palette.primary.main}30`
                    : '1px solid transparent',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(25, 118, 210, 0.04)',
                    transform: 'translateX(4px)',
                    borderColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(25, 118, 210, 0.2)',
                  }
                }}
                onClick={isMobile ? toggleDrawer : null}
              >
                <ListItemIcon sx={{ 
                  color: isActive 
                    ? theme.palette.primary.main 
                    : theme.palette.text.secondary,
                  minWidth: 40,
                  transition: 'all 0.3s ease',
                }}>
                  {icon}
                </ListItemIcon>
                <ListItemText 
                  primary={text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: isActive ? 600 : 500,
                      color: isActive 
                        ? theme.palette.primary.main 
                        : theme.palette.text.primary,
                      fontSize: '0.9rem',
                    }
                  }}
                />
              </ListItem>
            );
          })}

          {/* Divider */}
          <Divider sx={{ 
            my: 2, 
            mx: 1,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.08)',
          }} />

          {/* Reports Section */}
          <ListItem 
            onClick={toggleReports} 
            sx={{ 
              cursor: 'pointer',
              borderRadius: 2,
              mb: 0.5,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(25, 118, 210, 0.04)',
                transform: 'translateX(4px)',
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: theme.palette.secondary.main,
              minWidth: 40,
            }}>
              <Analytics />
            </ListItemIcon>
            <ListItemText 
              primary="Reports" 
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: '0.9rem',
                }
              }}
            />
            {reportsOpen ? 
              <ExpandLess sx={{ color: theme.palette.text.secondary }} /> : 
              <ExpandMore sx={{ color: theme.palette.text.secondary }} />
            }
          </ListItem>
          
          <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 1 }}>
              {reportItems.map((item) => {
                const isSelected = selectedReport === item.type;
                return (
                  <ListItem
                    button
                    key={item.text}
                    onClick={() => handleReportClick(item.type)}
                    selected={isSelected}
                    sx={{ 
                      pl: 3,
                      borderRadius: 2,
                      mb: 0.5,
                      ml: 1,
                      background: isSelected 
                        ? theme.palette.mode === 'dark' 
                          ? 'linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(156, 39, 176, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(156, 39, 176, 0.12) 0%, rgba(156, 39, 176, 0.06) 100%)'
                        : 'transparent',
                      border: isSelected 
                        ? `1px solid ${theme.palette.secondary.main}30`
                        : '1px solid transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(156, 39, 176, 0.04)',
                        transform: 'translateX(6px)',
                        borderColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(156, 39, 176, 0.2)',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: isSelected 
                        ? theme.palette.secondary.main 
                        : theme.palette.text.secondary,
                      minWidth: 36,
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: isSelected ? 600 : 500,
                          color: isSelected 
                            ? theme.palette.secondary.main 
                            : theme.palette.text.primary,
                          fontSize: '0.85rem',
                        }
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
